// @ts-check
// Conteúdo dos dropdowns de filtro e ordenação da barra de view (doc 06, 10.4 e 12).
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import {
  DropdownMenuHeader,
  DropdownMenuItems,
  DropdownMenuSearchInput,
  DropdownMenuSectionLabel,
  DropdownMenuSeparator,
  MenuItem,
  useKeyboardSelection,
} from '../../ui/dropdown.js';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { RatingStars } from '../object-record/field-display.js';
import { RecordPickerList } from '../object-record/record-picker.js';
import {
  FIELD_TYPE,
  getFieldIcon,
  getSortedOptions,
} from '../../lib/field-metadata.js';
import {
  formatRelativeDateLabel,
  getOperandsForField,
  getSubFieldLabel,
  operandNeedsValue,
  parseListValue,
} from '../../lib/view-filters.js';
import {
  RELATIVE_DATE_UNITS,
  parseRelativeDateValue,
  stringifyRelativeDateValue,
} from '../../../../shared/filter/relative-date.js';
import { normalizeSearchText } from '../../lib/utils.js';
import { scopeStageOptions } from '../../data/stage-scope.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../lib/view-filters.js').ViewFilter} ViewFilter
 */

// ícones dos sub-campos, como o ICON_NAME_BY_SUB_FIELD do Twenty (doc 06, seção 10.4)
const SUB_FIELD_ICONS = /** @type {Record<string, string>} */ ({
  firstName: 'IconTextSize', lastName: 'IconTextSize', primaryEmail: 'IconMail', additionalEmails: 'IconMailPlus',
  primaryPhoneNumber: 'IconPhone', additionalPhones: 'IconPhonePlus', primaryLinkUrl: 'IconLink', primaryLinkLabel: 'IconTextSize',
  amountMicros: 'IconNumber', currencyCode: 'IconCurrencyDollar',
});

/**
 * Submenu de sub-campos de um campo composto: "qualquer sub-campo" e cada sub-campo.
 * @param {{ field: FieldMetadata, subFields: readonly string[], onSelect: (subFieldName: string | null) => void, onBack: () => void, allowAny?: boolean }} props
 */
function SubFieldSelectMenu({ field, subFields, onSelect, onBack, allowAny = true }) {
  const items = /** @type {Array<string | null>} */ ([...(allowAny ? [null] : []), ...subFields]);
  const { focusedIndex, handleKeyDown } = useKeyboardSelection(items.length, (index) => onSelect(items[index]));
  const label = translateMetadataLabel(field.label);
  return html`
    <${DropdownMenuHeader} title=${label} onBack=${onBack} />
    <div tabindex="-1" onKeyDown=${handleKeyDown} ref=${(/** @type {HTMLDivElement | null} */ element) => element && !element.dataset.focused && (element.dataset.focused = 'true', requestAnimationFrame(() => element.focus({ preventScroll: true })))}>
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${items.map((subFieldName, index) => html`<${MenuItem}
          key=${subFieldName ?? 'any'}
          icon=${subFieldName ? SUB_FIELD_ICONS[subFieldName] ?? (field.type === 'ADDRESS' ? 'IconMapPin' : getFieldIcon(field)) : 'IconApps'}
          text=${subFieldName ? getSubFieldLabel(subFieldName) : t('viewsFilters.anySubField', { field: label })}
          focused=${focusedIndex === index}
          onClick=${() => onSelect(subFieldName)}
        />`)}
      <//>
    </div>
  `;
}

/**
 * Submenu de uma relação do público (contrato 09 de público, seção 5): o próprio registro e os campos do destino; um
 * campo composto do destino abre os sub-campos.
 * @param {{ relationField: FieldMetadata, targets: FieldMetadata[], getTargetSubFields?: (relationField: FieldMetadata, targetField: FieldMetadata) => readonly string[],
 *   onSelect: (field: FieldMetadata, subFieldName: string | null, targetField: FieldMetadata | null) => void, onBack: () => void }} props
 */
function RelationTargetSelectMenu({ relationField, targets, getTargetSubFields, onSelect, onBack }) {
  const [compositeTarget, setCompositeTarget] = useState(/** @type {FieldMetadata | null} */ (null));
  const items = /** @type {Array<FieldMetadata | null>} */ ([null, ...targets]);
  const subFieldsOf = (/** @type {FieldMetadata} */ target) => getTargetSubFields?.(relationField, target) ?? [];
  const choose = (/** @type {FieldMetadata | null} */ target) => {
    if (!target) onSelect(relationField, null, null);
    else if (subFieldsOf(target).length > 0) setCompositeTarget(target);
    else onSelect(relationField, null, target);
  };
  const { focusedIndex, handleKeyDown } = useKeyboardSelection(items.length, (index) => choose(items[index]));
  const relationLabel = translateMetadataLabel(relationField.label);
  if (compositeTarget) {
    return html`<${SubFieldSelectMenu} field=${compositeTarget} subFields=${subFieldsOf(compositeTarget)} allowAny=${false}
      onBack=${() => setCompositeTarget(null)} onSelect=${(subFieldName) => onSelect(relationField, subFieldName, compositeTarget)} />`;
  }
  return html`
    <${DropdownMenuHeader} title=${relationLabel} onBack=${onBack} />
    <div tabindex="-1" onKeyDown=${handleKeyDown} ref=${(/** @type {HTMLDivElement | null} */ element) => element && !element.dataset.focused && (element.dataset.focused = 'true', requestAnimationFrame(() => element.focus({ preventScroll: true })))}>
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${items.map((target, index) => html`<${MenuItem}
          key=${target?.id ?? 'record'}
          icon=${target ? getFieldIcon(target) : getFieldIcon(relationField)}
          text=${target ? translateMetadataLabel(target.label) : t('audience.relations.recordItself', { relation: relationLabel })}
          focused=${focusedIndex === index}
          hasSubMenu=${Boolean(target) && subFieldsOf(/** @type {FieldMetadata} */ (target)).length > 0}
          onClick=${() => choose(target)}
        />`)}
      <//>
    </div>
  `;
}

/**
 * Lista de campos com busca, separada em visíveis e ocultos na view. Com `getSubFields`, campos compostos abrem o
 * submenu dos sub-campos (nome e sobrenome, cidade, estado, CEP, valor e moeda). Com `getRelationTargets` (público),
 * uma relação do catálogo abre o próprio registro e os campos do destino.
 * @param {{ title: string, fields: FieldMetadata[], visibleFieldIds: Set<string>,
 *   onSelect: (field: FieldMetadata, subFieldName?: string | null, targetField?: FieldMetadata | null) => void,
 *   onClose: () => void, header?: any, getSubFields?: (field: FieldMetadata) => readonly string[], allowAnySubField?: boolean,
 *   getRelationTargets?: (field: FieldMetadata) => FieldMetadata[] | null,
 *   getTargetSubFields?: (relationField: FieldMetadata, targetField: FieldMetadata) => readonly string[] }} props
 */
export function FieldSelectMenu({ title, fields, visibleFieldIds, onSelect, onClose, header, getSubFields, allowAnySubField = true, getRelationTargets, getTargetSubFields }) {
  const [searchText, setSearchText] = useState('');
  const [compositeField, setCompositeField] = useState(/** @type {FieldMetadata | null} */ (null));
  const [relationField, setRelationField] = useState(/** @type {FieldMetadata | null} */ (null));
  const choose = (/** @type {FieldMetadata} */ field) => {
    const targets = getRelationTargets?.(field) ?? null;
    if (targets) {
      setRelationField(field);
      return;
    }
    const subFields = getSubFields?.(field) ?? [];
    if (subFields.length > 0) setCompositeField(field);
    else onSelect(field, null);
  };
  const matchingFields = fields.filter((field) =>
    normalizeSearchText(translateMetadataLabel(field.label)).includes(normalizeSearchText(searchText)),
  );
  const visibleFields = matchingFields.filter((field) => visibleFieldIds.has(field.id));
  const hiddenFields = matchingFields.filter((field) => !visibleFieldIds.has(field.id));
  const orderedFields = [...visibleFields, ...hiddenFields];
  const { focusedIndex, setFocusedIndex, handleKeyDown } = useKeyboardSelection(orderedFields.length, (index) => choose(orderedFields[index]));

  const renderField = (/** @type {FieldMetadata} */ field) => {
    const index = orderedFields.indexOf(field);
    return html`<${MenuItem}
      key=${field.id}
      icon=${getFieldIcon(field)}
      text=${translateMetadataLabel(field.label)}
      focused=${focusedIndex === index}
      hasSubMenu=${(getSubFields?.(field) ?? []).length > 0 || Boolean(getRelationTargets?.(field))}
      onClick=${() => choose(field)}
    />`;
  };

  if (relationField) {
    return html`<${RelationTargetSelectMenu}
      relationField=${relationField}
      targets=${getRelationTargets?.(relationField) ?? []}
      getTargetSubFields=${getTargetSubFields}
      onBack=${() => setRelationField(null)}
      onSelect=${(field, subFieldName, targetField) => onSelect(field, subFieldName, targetField)}
    />`;
  }

  if (compositeField) {
    return html`<${SubFieldSelectMenu}
      field=${compositeField}
      subFields=${getSubFields?.(compositeField) ?? []}
      allowAny=${allowAnySubField}
      onBack=${() => setCompositeField(null)}
      onSelect=${(subFieldName) => onSelect(compositeField, subFieldName)}
    />`;
  }

  return html`
    <${DropdownMenuHeader} title=${title} onClose=${onClose} />
    ${header ?? null}
    <${DropdownMenuSearchInput}
      value=${searchText}
      placeholder=${t('viewBar.searchFields')}
      onChange=${(value) => {
        setSearchText(value);
        setFocusedIndex(0);
      }}
      onKeyDown=${handleKeyDown}
    />
    <${DropdownMenuSeparator} />
    <${DropdownMenuItems} hasMaxHeight=${false}>
      ${orderedFields.length === 0 ? html`<div class="menu-item-empty">${t('common.noResults')}</div>` : null}
      ${visibleFields.length > 0 ? html`<${DropdownMenuSectionLabel} label=${t('viewBar.visibleFields')} />` : null}
      ${visibleFields.map(renderField)}
      ${hiddenFields.length > 0 ? html`<${DropdownMenuSectionLabel} label=${t('viewBar.hiddenFields')} />` : null}
      ${hiddenFields.map(renderField)}
    <//>
  `;
}

/**
 * Editor de um filtro: seletor de operando e entrada de valor por tipo. No público, a regra de relação passa o campo de
 * destino em `field`, o título pronto e, em um para muitos, os operandos e os rótulos próprios.
 * @param {{ filter: ViewFilter, field: FieldMetadata, onChange: (changes: Partial<ViewFilter>) => void, onClose: () => void,
 *   operands?: readonly string[], operandLabel?: (operand: string) => string, title?: string }} props
 */
export function FilterEditor({ filter, field, onChange, onClose, operands: operandsOverride, operandLabel = (operand) => t(`filters.operands.${operand}`), title: titleOverride }) {
  const [isOperandMenuOpen, setIsOperandMenuOpen] = useState(false);
  const operands = operandsOverride ?? getOperandsForField(field, filter.subFieldName ?? null);
  const title = titleOverride ?? (filter.subFieldName
    ? `${translateMetadataLabel(field.label)} / ${getSubFieldLabel(filter.subFieldName)}`
    : translateMetadataLabel(field.label));

  return html`
    <${DropdownMenuHeader} title=${title} onClose=${onClose} />
    <div class="filter-operand-select">
      <button type="button" class="inner-select" onClick=${() => setIsOperandMenuOpen(!isOperandMenuOpen)}>
        <span>${operandLabel(filter.operand)}</span>
        <${Icon} name="IconChevronDown" size=${14} />
      </button>
      ${isOperandMenuOpen
        ? html`<div class="inner-select-menu">
            <${DropdownMenuItems}>
              ${operands.map(
                (operand) => html`<${MenuItem}
                  key=${operand}
                  text=${operandLabel(operand)}
                  selected=${operand === filter.operand}
                  onClick=${() => {
                    // data relativa começa em "últimos 7 dias"; sair dela limpa o valor, que não é uma data
                    const changesRelative = (operand === 'IS_RELATIVE') !== (filter.operand === 'IS_RELATIVE');
                    const value = operand === 'IS_RELATIVE' ? (filter.operand === 'IS_RELATIVE' ? filter.value : DEFAULT_RELATIVE_DATE_VALUE) : '';
                    onChange({ operand, ...(operandNeedsValue(operand) && !changesRelative ? {} : { value, displayValue: '' }) });
                    setIsOperandMenuOpen(false);
                  }}
                />`,
              )}
            <//>
          </div>`
        : null}
    </div>
    ${operandNeedsValue(filter.operand) && !isOperandMenuOpen
      ? html`<${DropdownMenuSeparator} /><${FilterValueInput} filter=${filter} field=${field} onChange=${onChange} onClose=${onClose} />`
      : null}
  `;
}

/**
 * @param {{ filter: ViewFilter, field: FieldMetadata, onChange: (changes: Partial<ViewFilter>) => void, onClose: () => void }} props
 */
function FilterValueInput({ filter, field, onChange, onClose }) {
  const [optionSearch, setOptionSearch] = useState('');
  if (filter.operand === 'IS_RELATIVE') return html`<${RelativeDateInput} value=${filter.value} onChange=${(value) => onChange({ value, displayValue: '' })} />`;
  if (field.type === FIELD_TYPE.CURRENCY && filter.subFieldName === 'currencyCode') {
    return html`<div class="filter-value-input">
      <input type="text" class="text-input" placeholder=${t('viewsFilters.currencyCodePlaceholder')} value=${filter.value ?? ''} ref=${autoFocus}
        onInput=${(event) => onChange({ value: event.currentTarget.value.toUpperCase() })} onKeyDown=${(event) => event.key === 'Enter' && onClose()} />
    </div>`;
  }
  switch (field.type) {
    case FIELD_TYPE.SELECT:
    case FIELD_TYPE.MULTI_SELECT: {
      const selectedValues = parseListValue(filter.value);
      // chips da etapa do card: só as etapas que o papel vê (contrato de equipe, seção 3.2)
      const options = scopeStageOptions(field, getSortedOptions(field), 'FILTER').filter((option) =>
        normalizeSearchText(translateMetadataLabel(option.label)).includes(normalizeSearchText(optionSearch)),
      );
      return html`
        <${DropdownMenuSearchInput} value=${optionSearch} placeholder=${t('common.search')} onChange=${setOptionSearch} />
        <${DropdownMenuItems} hasMaxHeight=${true}>
          ${options.map(
            (option) => html`<${MenuItem}
              key=${option.value}
              multiSelect=${true}
              selected=${selectedValues.includes(option.value)}
              text=${html`<${Tag} color=${option.color} text=${translateMetadataLabel(option.label)} />`}
              onClick=${() => {
                const nextValues = selectedValues.includes(option.value)
                  ? selectedValues.filter((value) => value !== option.value)
                  : [...selectedValues, option.value];
                onChange({ value: nextValues.length ? JSON.stringify(nextValues) : '', displayValue: '' });
              }}
            />`,
          )}
        <//>
      `;
    }
    case FIELD_TYPE.RELATION: {
      const selectedIds = parseListValue(filter.value);
      const labels = new Map(
        (filter.displayValue ? filter.displayValue.split(', ') : []).map((label, index) => [selectedIds[index], label]),
      );
      return html`<${RecordPickerList}
        objectNameSingular=${field.relation?.targetObjectNameSingular ?? ''}
        selectedIds=${selectedIds}
        multiple=${true}
        allowEmpty=${false}
        preselectedRecords=${selectedIds.map((id) => ({ id, label: labels.get(id) ?? '' }))}
        onSelect=${(record, isSelected) => {
          if (!record) return;
          const nextLabels = new Map(labels);
          let nextIds = selectedIds;
          if (isSelected) {
            nextIds = [...selectedIds, record.id];
            nextLabels.set(record.id, record.label);
          } else {
            nextIds = selectedIds.filter((id) => id !== record.id);
            nextLabels.delete(record.id);
          }
          onChange({
            value: nextIds.length ? JSON.stringify({ selectedRecordIds: nextIds }) : '',
            displayValue: nextIds.map((id) => nextLabels.get(id) ?? '').join(', '),
          });
        }}
      />`;
    }
    case FIELD_TYPE.BOOLEAN:
      return html`<${DropdownMenuItems}>
        ${['true', 'false'].map(
          (value) => html`<${MenuItem}
            key=${value}
            icon=${value === 'true' ? 'IconCheck' : 'IconX'}
            text=${value === 'true' ? t('common.true') : t('common.false')}
            selected=${filter.value === value}
            onClick=${() => {
              onChange({ value });
              onClose();
            }}
          />`,
        )}
      <//>`;
    case FIELD_TYPE.RATING:
      return html`<div class="filter-value-input">
        <${RatingStars}
          value=${Number(String(filter.value).replace('RATING_', '')) || 0}
          onSelect=${(rating) => onChange({ value: rating ? `RATING_${rating}` : '' })}
        />
      </div>`;
    case FIELD_TYPE.DATE:
    case FIELD_TYPE.DATE_TIME:
      return html`<div class="filter-value-input">
        <input
          type="date"
          class="text-input"
          value=${String(filter.value ?? '').slice(0, 10)}
          ref=${autoFocus}
          onInput=${(event) => onChange({ value: event.currentTarget.value })}
          onKeyDown=${(event) => event.key === 'Enter' && onClose()}
        />
      </div>`;
    case FIELD_TYPE.NUMBER:
    case FIELD_TYPE.NUMERIC:
    case FIELD_TYPE.CURRENCY:
      return html`<div class="filter-value-input">
        <input
          type="text"
          inputmode="decimal"
          class="text-input"
          placeholder=${translateMetadataLabel(field.label)}
          value=${filter.value ?? ''}
          ref=${autoFocus}
          onInput=${(event) => onChange({ value: event.currentTarget.value })}
          onKeyDown=${(event) => event.key === 'Enter' && onClose()}
        />
      </div>`;
    default:
      return html`<div class="filter-value-input">
        <input
          type="text"
          class="text-input"
          placeholder=${translateMetadataLabel(field.label)}
          value=${filter.value ?? ''}
          ref=${autoFocus}
          onInput=${(event) => onChange({ value: event.currentTarget.value })}
          onKeyDown=${(event) => event.key === 'Enter' && onClose()}
        />
      </div>`;
  }
}

/**
 * @param {HTMLInputElement | null} element
 */
function autoFocus(element) {
  if (element && !element.dataset.focused) {
    element.dataset.focused = 'true';
    requestAnimationFrame(() => element.focus({ preventScroll: true }));
  }
}

export const DEFAULT_RELATIVE_DATE_VALUE = 'PAST_7_DAY';

// atalhos do seletor de data relativa: hoje, esta semana, este mês e os intervalos mais usados na captação
const RELATIVE_DATE_PRESETS = Object.freeze(['THIS__DAY', 'THIS__WEEK', 'THIS__MONTH', 'PAST_7_DAY', 'PAST_30_DAY', 'NEXT_7_DAY']);

/**
 * Seletor de data relativa (RelativeDatePickerHeader do Twenty): atalhos e a linha direção, quantidade e unidade.
 * O valor não guarda fuso: cada pessoa vê a view no próprio fuso.
 * @param {{ value: string, onChange: (value: string) => void }} props
 */
export function RelativeDateInput({ value, onChange }) {
  const parsed = parseRelativeDateValue(value) ?? parseRelativeDateValue(DEFAULT_RELATIVE_DATE_VALUE);
  const current = /** @type {NonNullable<typeof parsed>} */ (parsed);
  const update = (/** @type {Partial<typeof current>} */ changes) => {
    const next = { ...current, ...changes, timeZone: null, firstDayOfWeek: null };
    if (next.direction !== 'THIS' && !next.amount) next.amount = 1;
    onChange(stringifyRelativeDateValue(next));
  };
  const amount = current.amount ?? 1;
  return html`<div class="relative-date-input" data-testid="relative-date-input">
    <div class="relative-date-presets">
      ${RELATIVE_DATE_PRESETS.map((preset) => html`<button type="button" key=${preset} class="relative-date-preset"
        data-active=${stringifyRelativeDateValue(current) === preset ? 'true' : undefined} onClick=${() => onChange(preset)}>
        ${formatRelativeDateLabel(preset)}
      </button>`)}
    </div>
    <div class="relative-date-row">
      <select class="text-input" aria-label=${t('viewsFilters.relative.direction')} value=${current.direction}
        onChange=${(event) => update({ direction: /** @type {any} */ (event.currentTarget.value) })}>
        ${['PAST', 'THIS', 'NEXT'].map((direction) => html`<option value=${direction}>${t(`viewsFilters.relative.directions.${direction}`)}</option>`)}
      </select>
      <input type="number" min="1" max="999" class="text-input relative-date-amount" aria-label=${t('viewsFilters.relative.amount')}
        disabled=${current.direction === 'THIS'} value=${current.direction === 'THIS' ? '' : String(amount)} placeholder="-"
        onInput=${(event) => {
          const nextAmount = Math.floor(Number(event.currentTarget.value));
          if (Number.isInteger(nextAmount) && nextAmount >= 1 && nextAmount <= 999) update({ amount: nextAmount });
        }} />
      <select class="text-input" aria-label=${t('viewsFilters.relative.unit')} value=${current.unit}
        onChange=${(event) => update({ unit: /** @type {any} */ (event.currentTarget.value) })}>
        ${RELATIVE_DATE_UNITS.map((unit) => html`<option value=${unit}>${t(`viewsFilters.relative.units.${unit.toLowerCase()}`, { count: current.direction === 'THIS' ? 1 : amount })}</option>`)}
      </select>
    </div>
    <div class="relative-date-summary">${formatRelativeDateLabel(stringifyRelativeDateValue(current))}</div>
  </div>`;
}
